import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faCalendarDay,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { JourneyStepInput } from "../../../types/journey";
import { JourneyBasicInfo } from "./JourneyBasicInfoStep";

// Game type definition
interface GameType {
  id: string;
  name: string;
  title?: string;
  emoji?: string;
  description?: string;
}

interface ReviewStepProps {
  journeyInfo: JourneyBasicInfo;
  touchpoints: JourneyStepInput[];
  gameTypes: GameType[];
  onEditJourneyInfo: () => void;
  onEditTouchpoint: (index: number) => void;
}

/**
 * Final step in the journey creation wizard
 * Shows a summary of the journey and all touchpoints for review before submission
 */
const ReviewStep: React.FC<ReviewStepProps> = ({
  journeyInfo,
  touchpoints,
  gameTypes,
  onEditJourneyInfo,
  onEditTouchpoint,
}) => {
  // Helper to get game type name from id
  const getGameTypeName = (gameTypeId: string): string => {
    const gameType = gameTypes.find((g) => g.id === gameTypeId);
    const emoji = (gameType && gameType.emoji) || "🎮";
    const title = (gameType && gameType.title) || "Unknown game type";
    return `${emoji} ${title}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Review Your Journey
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Review the journey details before creating it.
        </p>
      </div>

      {/* Journey basic information */}
      <div className="bg-white shadow overflow-hidden rounded-md">
        <div className="px-4 py-4 sm:px-6 flex justify-between items-center bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">Journey Details</h4>
          <button
            type="button"
            onClick={onEditJourneyInfo}
            className="text-sm text-brand hover:text-brand-dark flex items-center"
          >
            <FontAwesomeIcon icon={faEdit} className="mr-1" />
            Edit
          </button>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{journeyInfo.name}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Touchpoints</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {touchpoints.length}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {journeyInfo.description || "No description provided"}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Touchpoints list - Compact view */}
      <div className="space-y-2">
        <h4 className="text-md font-medium text-gray-900">Touchpoints</h4>

        {touchpoints.map((touchpoint, index) => (
          <div
            key={index}
            className="bg-white shadow overflow-hidden rounded-md border border-gray-200"
          >
            {/* Touchpoint header with game type and edit button */}
            <div className="px-3 py-2 flex justify-between items-center bg-gray-50 border-b border-gray-200">
              <div className="flex items-center">
                <h5 className="text-sm font-medium text-gray-900">
                  {getGameTypeName(touchpoint.game_type_id)}
                </h5>
              </div>
              <button
                type="button"
                onClick={() => onEditTouchpoint(index)}
                className="text-xs text-brand hover:text-brand-dark flex items-center"
              >
                <FontAwesomeIcon icon={faEdit} className="mr-1" />
                Edit
              </button>
            </div>

            {/* Touchpoint content - Using flex for horizontal layout */}
            <div className="px-3 py-2">
              {/* Two-column layout for better space usage */}
              <div className="flex flex-wrap text-xs">
                {/* Left column with timing info */}
                <div className="w-1/2 flex items-center space-x-4 mb-1">
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faCalendarDay}
                      className="mr-1 text-gray-400"
                    />
                    <span className="text-gray-700">
                      Day {touchpoint.offset_days}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="mr-1 text-gray-400"
                    />
                    <span className="text-gray-700">
                      {touchpoint.offset_hours}:00{" "}
                      {touchpoint.offset_hours < 12 ? "AM" : "PM"}
                    </span>
                  </div>
                </div>

                {/* Right column with content info */}
                <div className="w-1/2 mb-1">
                  <span className="text-gray-500">Content: </span>
                  <span className="text-gray-700">
                    {touchpoint.content_selection_type === "surprise_me" &&
                      "Surprise Me"}
                    {touchpoint.content_selection_type === "category" &&
                      "Category"}
                    {touchpoint.content_selection_type === "custom" && (
                      <>
                        Custom
                        {touchpoint.custom_content &&
                          touchpoint.custom_content.length > 0 && (
                            <span className="text-xs ml-1 text-gray-500">
                              ({touchpoint.custom_content.length} item
                              {touchpoint.custom_content.length !== 1
                                ? "s"
                                : ""}
                              )
                            </span>
                          )}
                      </>
                    )}
                  </span>

                  {/* Options as badges */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {touchpoint.game_config?.allow_rematch && (
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        Rematch enabled
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Custom message if provided, with smaller padding and font */}
              {touchpoint.custom_message && (
                <div className="mt-1">
                  <p className="text-xs text-gray-500">Custom Message:</p>
                  <p className="text-xs text-gray-900 bg-gray-50 p-1.5 rounded mt-0.5 line-clamp-2">
                    {touchpoint.custom_message}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewStep;
